<template>
  <section class="pointer-events-auto flex w-[calc(100vw-5.5rem)] shrink-0 flex-col overflow-hidden rounded-t-lg border bg-background shadow-2xl sm:w-80 lg:w-88" :class="isMinimized ? 'h-11' : 'h-120'" @click="activateConversation">
    <header class="flex h-11 shrink-0 items-center gap-2 border-b bg-muted/40 px-3">
      <button type="button" class="flex min-w-0 flex-1 items-center gap-2 text-left" :disabled="isUpdatingWindow" @click.stop="toggleMinimized">
        <Avatar size="sm">
          <AvatarImage v-if="conversation.avatar_path" :src="conversation.avatar_path" />
          <AvatarFallback>{{ conversationInitials }}</AvatarFallback>
        </Avatar>
        <span class="min-w-0 flex-1 truncate text-sm font-semibold">{{ conversationTitle }}</span>
        <Badge v-if="conversation.current_participant?.unread_count > 0" variant="secondary" class="shrink-0 px-1.5 py-0 text-[10px]">
          {{ conversation.current_participant.unread_count }}
        </Badge>
      </button>

      <RouterLink :to="{ name: 'ChatShowPage', params: { conversation: conversation.id } }" class="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" aria-label="Open full conversation" title="Open full conversation">
        <Maximize2 class="h-3.5 w-3.5" />
      </RouterLink>
      <Button variant="ghost" size="icon-sm" :aria-label="isMinimized ? 'Restore chat window' : 'Minimize chat window'" :title="isMinimized ? 'Restore chat window' : 'Minimize chat window'" :disabled="isUpdatingWindow" @click.stop="toggleMinimized">
        <ChevronUp v-if="isMinimized" class="h-4 w-4" />
        <Minus v-else class="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Close chat window" title="Close chat window" :disabled="isUpdatingWindow" @click.stop="closeWindow">
        <LoaderCircle v-if="isUpdatingWindow" class="h-4 w-4 animate-spin" />
        <X v-else class="h-4 w-4" />
      </Button>
    </header>

    <template v-if="!isMinimized">
      <Alert v-if="windowError" variant="destructive" class="m-2 mb-0 py-2">
        <AlertDescription class="text-xs">
          {{ windowError }}
        </AlertDescription>
      </Alert>

      <ChatMessageThread :conversation-id="conversation.id" :messages="messages" :current-user-id="authStore.user?.id" :participants="activeParticipants" :loading="isLoadingMessages" @load-older="loadOlderMessages" @message-action-started="clearWindowError" @message-action-failed="setWindowError" />
      <ChatMessageComposer :conversation-id="conversation.id" :hide-send-button="true" />
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ChevronUp, LoaderCircle, Maximize2, Minus, X } from 'lucide-vue-next'
import { Alert, AlertDescription } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../../../resources/js/components/ui/avatar/index.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { useAuthStore } from '../../../../../../../resources/js/stores/auth.js'
import { useChatStore } from '../../stores/chatStore.js'
import { useConversationParticipantStore } from '../../stores/conversationParticipantStore.js'
import { useMessageStore } from '../../stores/messageStore.js'
import ChatMessageComposer from '../messages/ChatMessageComposer.vue'
import ChatMessageThread from '../messages/ChatMessageThread.vue'

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
})

const authStore = useAuthStore()
const chatStore = useChatStore()
const messageStore = useMessageStore()
const participantStore = useConversationParticipantStore()

const isLoadingMessages = ref(false)
const windowError = ref('')

const messages = computed(() => messageStore.getMessagesByConversationId(props.conversation.id))
const activeParticipants = computed(() => props.conversation.participants?.filter((participant) => participant.left_at === null) || [])
const isMinimized = computed(() => props.conversation.current_participant?.chat_window_state === 'minimized')
const isUpdatingWindow = computed(() => participantStore.updatingWindowConversationIds.includes(Number(props.conversation.id)))
const conversationTitle = computed(() => {
  if (props.conversation.title) {
    return props.conversation.title
  }

  const otherParticipant = props.conversation.participants?.find((participant) => Number(participant.user_id) !== Number(authStore.user?.id))

  return otherParticipant?.user?.name || 'Direct conversation'
})
const conversationInitials = computed(() => {
  return conversationTitle.value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'C'
})

const loadMessages = async () => {
  isLoadingMessages.value = true
  windowError.value = ''

  try {
    await messageStore.fetchMessages(props.conversation.id, { clear: true })
  } catch (error) {
    windowError.value = errorMessageFrom(error, 'Unable to load messages.')
  } finally {
    isLoadingMessages.value = false
  }
}

const loadOlderMessages = async () => {
  isLoadingMessages.value = true
  windowError.value = ''

  try {
    await messageStore.fetchOlderMessages(props.conversation.id)
  } catch (error) {
    windowError.value = errorMessageFrom(error, 'Unable to load older messages.')
  } finally {
    isLoadingMessages.value = false
  }
}

const activateConversation = () => {
  if (isMinimized.value) {
    return
  }

  chatStore.setActiveConversation(props.conversation.id)
  markLatestMessageRead()
}

const toggleMinimized = async () => {
  windowError.value = ''
  const state = isMinimized.value ? 'open' : 'minimized'

  try {
    await participantStore.setChatWindowState(props.conversation.id, state)

    if (state === 'open') {
      chatStore.setActiveConversation(props.conversation.id)
      markLatestMessageRead()
    } else if (Number(chatStore.activeConversationId) === Number(props.conversation.id)) {
      chatStore.clearActiveConversation()
    }
  } catch (error) {
    windowError.value = errorMessageFrom(error, 'Unable to update this chat window.')
  }
}

const closeWindow = async () => {
  windowError.value = ''

  try {
    await participantStore.setChatWindowState(props.conversation.id, 'closed')

    if (Number(chatStore.activeConversationId) === Number(props.conversation.id)) {
      chatStore.clearActiveConversation()
    }
  } catch (error) {
    windowError.value = errorMessageFrom(error, 'Unable to close this chat window.')
  }
}

const markLatestMessageRead = async () => {
  const messageId = Number(messages.value.at(-1)?.id)

  if (!Number.isInteger(messageId) || messageId < 1 || Number(props.conversation.current_participant?.unread_count) < 1) {
    return
  }

  await participantStore.markConversationRead(props.conversation.id, { message_id: messageId }).catch(() => null)
}

const clearWindowError = () => {
  windowError.value = ''
}

const setWindowError = (message) => {
  windowError.value = message
}

const errorMessageFrom = (error, fallback) => {
  return error?.response?.data?.message || error?.message || fallback
}

watch(
  () => messages.value.at(-1)?.id,
  () => {
    if (!isMinimized.value && Number(chatStore.activeConversationId) === Number(props.conversation.id)) {
      markLatestMessageRead()
    }
  }
)

onMounted(() => {
  chatStore.subscribeToConversation(props.conversation.id)

  if (messages.value.length === 0) {
    loadMessages()
  }
})
</script>
